import { defineStore } from 'pinia'
import { useMutation } from '@tanstack/vue-query'
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import api from '@/services/axios'
import { ROUTES } from '@/router/routeNames'
import type { User } from '@/types'
import {
  registerPushNotificationServiceWorker,
  registerForPushNotifications,
  requestNotificationPermission,
} from '@/pwa'
import mixpanel from '@/services/mixpanel'
export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const route = useRoute()

  const isAuthenticated = ref(false)
  const user = ref<User | null>(null)

  const getUser = async (force = false) => {
    if (user.value && !force) {
      return user.value
    }
    const res = await api.get('/api/user')
    mixpanel.identify(res.data.id)
    mixpanel.people.set({
      $name: res.data.name,
      $email: res.data.email,
      $phone: res.data.phone,
      $address: res.data.address,
      $created: res.data.created_at,
      $last_login: res.data.last_login_at,
      $last_seen: res.data.last_seen_at,
    })
    user.value = res.data
    isAuthenticated.value = true
    return user.value
  }

  const postLogin = () => {
    mixpanel.track('Login Successful')
    // Always force a fresh user data fetch after login
    // This ensures we have the correct user role and permissions

    // First get fresh user data
    getUser(true).then(() => {
      // Redirect immediately without waiting for push notifications
      if (route.query.redirect) {
        router.push(route.query.redirect as string)
      } else {
        router.push({
          name: ROUTES.DASHBOARD,
        })
      }
    })

    // Setup push notifications in the background without blocking the redirect
    const setupPushNotifications = async () => {
      try {
        // Register push notification service worker
        await registerPushNotificationServiceWorker()

        // Request notification permission
        const permission = await requestNotificationPermission()

        // If permission is granted, register for push notifications
        if (permission === 'granted') {
          const registered = await registerForPushNotifications()
        }
      } catch (error) {
      }
    }

    // Run push notifications setup in the background
    setupPushNotifications()
  }

  const getResetPasswordToken = async (email: string) => {
    const res = await api.get(`/get-reset-token/${email}`)
    return res.data
  }

  const resetPasswordMutation = useMutation({
    mutationFn: async (credentials: {
      email: string
      password: string
      confirmPassword: string
      token: string
    }) => {
      // await api.get('/sanctum/csrf-cookie')
      return api.post('/reset-password', {
        email: credentials.email,
        password: credentials.password,
        token: credentials.token,
        password_confirmation: credentials.confirmPassword,
      })
    },
  })

  const resetPassword = () => {
    const { isError, error, isPending, mutate, mutateAsync } = resetPasswordMutation
    return { isPending, error, isError, mutate, mutateAsync }
  }

  const loginMutation = useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      await api.get('/sanctum/csrf-cookie')
      return api.post('/login', credentials)
    },
    onSuccess: postLogin,
  })
  const forgotPasswordMutation = useMutation({
    mutationFn: async (credentials: { email: string }) => {
      await api.get('/sanctum/csrf-cookie')
      return api.post('/forgot-password', credentials)
    },
  })

  const login = () => {
    const { isError, error, isPending, mutate } = loginMutation
    return { isPending, error, isError, mutate }
  }

  const forgotPassword = () => {
    const { isError, error, isPending, mutate, mutateAsync } = forgotPasswordMutation
    return { isPending, error, isError, mutate, mutateAsync }
  }

  const logoutMutation = useMutation({
    mutationFn: () => api.post('/logout'),
    onSuccess: () => {
      // Completely reset user state
      user.value = null
      isAuthenticated.value = false

      // Clear any cached API responses that might contain user data
      // This helps prevent stale data when logging in as a different user
      try {
        // Clear Vue Query cache if available
        if (window.$nuxt?.$vueQueryClient) {
          window.$nuxt.$vueQueryClient.clear()
        }

        // Clear localStorage items that might contain user data
        localStorage.removeItem('user')
        localStorage.removeItem('auth')

        // Clear sessionStorage items that might contain user data
        sessionStorage.removeItem('user')
        sessionStorage.removeItem('auth')

        // Force reload all API data on next request
        api.defaults.headers.common['Cache-Control'] = 'no-cache, no-store, must-revalidate'
      } catch (error) {
      }

      // Navigate to login page
      router.push({ name: ROUTES.LOGIN })
    },
  })

  const logout = async () => {
    try {
      return await logoutMutation.mutateAsync()
    } catch (error) {
      // Reset user state anyway to prevent being stuck in a logged-in state
      user.value = null
      isAuthenticated.value = false
      router.push({ name: ROUTES.LOGIN })
      throw error
    }
  }

  // Check if user's email is verified
  const isEmailVerified = computed(() => {
    return user.value && user.value.email_verified_at !== null
  })

  // Resend email verification notification
  const resendEmailVerificationMutation = useMutation({
    mutationFn: () => api.post('/api/email/verification-notification'),
    onSuccess: () => {
      // Success handling is done in the component
    },
  })

  const resendEmailVerification = () => {
    return resendEmailVerificationMutation.mutate()
  }

  return {
    login,
    getUser,
    user,
    postLogin,
    isAuthenticated,
    resetPassword,
    logout,
    isEmailVerified,
    resendEmailVerification,
  }
})
